import { useState, useCallback, useEffect } from 'react'
import {
  apiAgentGetProfile,
  apiAgentRotateNexbitApiKey,
  apiAgentRotateLaunchSecret,
  ApiRequestError,
  type AdminPartner,
} from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, Copy, Check, RefreshCw, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)
  async function handleCopy() {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <Button
      variant="outline"
      size="sm"
      className="h-7 gap-1.5 font-mono text-xs"
      onClick={() => void handleCopy()}
    >
      {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
      {label}
    </Button>
  )
}

function KeyCard({
  label,
  desc,
  masked,
  newValue,
  rotating,
  onRotate,
  rotateLabel,
  rotateWarning,
  newKeyNotice,
  copyNow,
}: {
  label: string
  desc: string
  masked: string
  newValue: string | null
  rotating: boolean
  onRotate: () => void
  rotateLabel: string
  rotateWarning: string
  newKeyNotice: string
  copyNow: string
}) {
  return (
    <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
      <div className="px-4 py-2.5 bg-secondary">
        <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">{label}</span>
      </div>
      <div className="p-4">
        <p className="font-mono text-[10px] text-muted-foreground mb-3">{desc}</p>

        <code className="block font-mono text-xs bg-secondary/40 border border-border rounded-[var(--radius-sm)] px-3 py-2 text-foreground break-all mb-4">
          {masked}
        </code>

        {newValue && (
          <div className="border border-success/40 rounded-[var(--radius-sm)] bg-success/5 p-3 mb-4">
            <p className="font-mono text-[10px] text-success uppercase tracking-widest mb-1.5">{newKeyNotice}</p>
            <code className="block font-mono text-xs text-foreground break-all mb-2">{newValue}</code>
            <CopyButton value={newValue} label={copyNow} />
          </div>
        )}

        <div className="flex items-start gap-3">
          <Button
            variant="outline"
            size="sm"
            className="font-mono text-xs h-8 gap-1.5 shrink-0"
            onClick={onRotate}
            disabled={rotating}
          >
            <RefreshCw className={cn('h-3 w-3', rotating && 'animate-spin')} />
            {rotateLabel}
          </Button>
          <p className="font-mono text-[10px] text-muted-foreground/70 pt-1.5">{rotateWarning}</p>
        </div>
      </div>
    </div>
  )
}

export default function AgentKeysPage() {
  const t = useT()
  const [profile, setProfile] = useState<AdminPartner | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rotatingNexbit, setRotatingNexbit] = useState(false)
  const [rotatingLaunch, setRotatingLaunch] = useState(false)
  const [newNexbitKey, setNewNexbitKey] = useState<string | null>(null)
  const [newLaunchSecret, setNewLaunchSecret] = useState<string | null>(null)
  const [confirmRotate, setConfirmRotate] = useState<'nexbit' | 'launch' | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setProfile(await apiAgentGetProfile())
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { void load() }, [load])

  async function handleRotateNexbit() {
    setConfirmRotate(null)
    setRotatingNexbit(true)
    setError(null)
    try {
      const { nexbitApiKey } = await apiAgentRotateNexbitApiKey()
      setNewNexbitKey(nexbitApiKey)
      setProfile((p) => p ? { ...p, nexbitApiKeyMasked: `${nexbitApiKey.slice(0, 8)}...` } : p)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.agentKeys.rotateSuccess)
    } finally {
      setRotatingNexbit(false)
    }
  }

  async function handleRotateLaunch() {
    setConfirmRotate(null)
    setRotatingLaunch(true)
    setError(null)
    try {
      const { launchSecret } = await apiAgentRotateLaunchSecret()
      setNewLaunchSecret(launchSecret)
      setProfile((p) => p ? { ...p, launchSecretMasked: `${launchSecret.slice(0, 8)}...` } : p)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.agentKeys.rotateSuccess)
    } finally {
      setRotatingLaunch(false)
    }
  }

  return (
    <div className="p-4 md:p-6 max-w-lg">
      <div className="mb-5">
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {t.agentKeys.title}
        </span>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-4">
          {[0, 1].map((i) => (
            <div key={i} className="border border-border rounded-[var(--radius)] bg-card p-4">
              <Skeleton className="h-3 w-24 mb-3" />
              <Skeleton className="h-9 w-full mb-3" />
              <Skeleton className="h-8 w-28" />
            </div>
          ))}
        </div>
      ) : profile ? (
        <div className="flex flex-col gap-4">
          <KeyCard
            label={t.agentKeys.nexbitApiKey}
            desc={t.agentKeys.nexbitApiKeyDesc}
            masked={profile.nexbitApiKeyMasked}
            newValue={newNexbitKey}
            rotating={rotatingNexbit}
            onRotate={() => setConfirmRotate('nexbit')}
            rotateLabel={t.agentKeys.rotate}
            rotateWarning={t.agentKeys.rotateWarning}
            newKeyNotice={t.agentKeys.newKeyNotice}
            copyNow={t.agentKeys.copyNow}
          />
          <KeyCard
            label={t.agentKeys.launchSecret}
            desc={t.agentKeys.launchSecretDesc}
            masked={profile.launchSecretMasked ?? '••••••••'}
            newValue={newLaunchSecret}
            rotating={rotatingLaunch}
            onRotate={() => setConfirmRotate('launch')}
            rotateLabel={t.agentKeys.rotate}
            rotateWarning={t.agentKeys.rotateWarning}
            newKeyNotice={t.agentKeys.newKeyNotice}
            copyNow={t.agentKeys.copyNow}
          />
        </div>
      ) : null}

      {/* Confirm dialog */}
      <Dialog open={confirmRotate !== null} onOpenChange={(v) => { if (!v) setConfirmRotate(null) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm uppercase tracking-wider flex items-center gap-2">
              <TriangleAlert className="h-4 w-4 text-destructive" />
              {t.agentKeys.rotate}
            </DialogTitle>
          </DialogHeader>
          <p className="font-mono text-xs text-muted-foreground">{t.agentKeys.rotateWarning}</p>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" size="sm" className="font-mono text-xs" onClick={() => setConfirmRotate(null)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="font-mono text-xs"
              onClick={() => void (confirmRotate === 'nexbit' ? handleRotateNexbit() : handleRotateLaunch())}
            >
              {t.agentKeys.rotate}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
